import React, { useMemo, useState, useCallback } from 'react';
import { Button } from '@crisellevaldez/speculo-ui';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsRight, Loader2 } from 'lucide-react';
import { formatNumber } from '../../utils/format';

interface OffsetPaginationProps {
  currentPage: number;
  totalItems: number | null;
  pageSize: number;
  onNextPage: () => void;
  onPrevPage: () => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  isLoading?: boolean;
  isTotalLoading?: boolean;
  hasMore?: boolean;
  label?: string;
}

const pageSizeOptions = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '500', label: '500' },
];

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  if (current <= 3) return [0, 1, 2, 3, 4, '...', total - 1];
  if (current >= total - 4)
    return [0, '...', total - 5, total - 4, total - 3, total - 2, total - 1];
  return [0, '...', current - 1, current, current + 1, '...', total - 1];
}

export const OffsetPagination: React.FC<OffsetPaginationProps> = ({
  currentPage,
  totalItems,
  pageSize,
  onNextPage,
  onPrevPage,
  onPageChange,
  onPageSizeChange,
  isLoading,
  isTotalLoading,
  hasMore: hasMoreFromPage,
  label = 'contacts',
}) => {
  const totalIsKnown = totalItems !== null;
  const totalPages = totalIsKnown ? Math.ceil(totalItems / pageSize) : currentPage + 1;
  const hasPrev = currentPage > 0;
  const hasNext = totalIsKnown ? currentPage < totalPages - 1 : Boolean(hasMoreFromPage);

  const handleNext = () => {
    if (onPageChange) {
      onPageChange(totalIsKnown ? Math.min(currentPage + 1, totalPages - 1) : currentPage + 1);
    } else onNextPage();
  };
  const handlePrev = () => {
    if (onPageChange) onPageChange(Math.max(currentPage - 1, 0));
    else onPrevPage();
  };
  const pageNumbers = useMemo(
    () => (totalIsKnown ? getPageNumbers(currentPage, totalPages) : [currentPage]),
    [currentPage, totalIsKnown, totalPages]
  );

  const [editValue, setEditValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const handleStartEdit = useCallback(() => {
    if (!onPageChange || !totalIsKnown) return;
    setEditValue(String(currentPage + 1));
    setIsEditing(true);
  }, [onPageChange, currentPage, totalIsKnown]);

  const handleCommitEdit = useCallback(() => {
    setIsEditing(false);
    if (!onPageChange) return;
    const parsed = parseInt(editValue, 10);
    if (isNaN(parsed) || parsed < 1) {
      onPageChange(0);
    } else if (parsed > totalPages) {
      onPageChange(totalPages - 1);
    } else {
      onPageChange(parsed - 1);
    }
  }, [editValue, onPageChange, totalPages]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleCommitEdit();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
      }
    },
    [handleCommitEdit]
  );

  if (totalIsKnown && totalItems === 0) return null;

  // How many pages there are is a property of the total, so until the count
  // lands the strip can only offer the page you are on. The spinner sits where
  // the rest of the pages will appear, so a short strip reads as still filling
  // in rather than as the end of the results.
  const countSpinner = !totalIsKnown && isTotalLoading && (
    <Loader2
      role="status"
      aria-label={`Calculating ${label} count`}
      className="w-4 h-4 mx-1 shrink-0 animate-spin text-muted-foreground"
    />
  );

  const pageIndicator = !totalIsKnown ? (
    <span className="flex items-center text-sm text-muted-foreground whitespace-nowrap">
      {countSpinner}
      Page {formatNumber(currentPage + 1)}
    </span>
  ) : onPageChange ? (
    isEditing ? (
      <input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={handleCommitEdit}
        onKeyDown={handleKeyDown}
        autoFocus
        className="w-10 h-7 text-center text-sm rounded border border-foreground/30 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />
    ) : (
      <button
        onClick={handleStartEdit}
        className="min-w-[36px] h-7 px-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors cursor-text"
        title="Click to jump to a page"
      >
        {formatNumber(currentPage + 1)} / {formatNumber(totalPages)}
      </button>
    )
  ) : (
    <span className="text-sm text-muted-foreground whitespace-nowrap">
      {formatNumber(currentPage + 1)} / {formatNumber(totalPages)}
    </span>
  );

  return (
    <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 md:px-5 py-2 sm:py-3 flex-wrap">
      {/* Total count */}
      <span className="text-xs sm:text-sm text-muted-foreground shrink-0">
        {totalIsKnown ? (
          <>
            <span className="font-semibold text-foreground">{formatNumber(totalItems)}</span>{' '}
            {label}
          </>
        ) : isTotalLoading ? (
          <>Calculating {label}…</>
        ) : (
          <>{label} count unavailable</>
        )}
      </span>

      {/* Mobile: Prev / Page X of Y / Next */}
      <div className="flex items-center gap-2 sm:hidden">
        <Button variant="outline" size="icon" onClick={handlePrev} disabled={!hasPrev || isLoading}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {pageIndicator}
        <Button variant="outline" size="icon" onClick={handleNext} disabled={!hasNext || isLoading}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Desktop: Prev + numbered + editable input + Next + Last */}
      <div className="hidden sm:flex items-center gap-1 shrink-0">
        <Button variant="outline" size="icon" onClick={handlePrev} disabled={!hasPrev || isLoading}>
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {countSpinner}

        {pageNumbers.map((p, i) =>
          p === '...' ? (
            <span key={`e-${i}`} className="px-1 text-muted-foreground text-sm select-none">
              …
            </span>
          ) : (
            <Button
              key={p}
              variant={p === currentPage ? 'primary' : 'outline'}
              size="sm"
              onClick={() => onPageChange?.(p as number)}
              disabled={isLoading || !onPageChange}
              className="min-w-[36px]"
            >
              {formatNumber((p as number) + 1)}
            </Button>
          )
        )}

        {/* Editable page jump — always visible as input for large page counts */}
        {onPageChange && totalPages > 7 && (
          <input
            type="text"
            value={isEditing ? editValue : ''}
            onChange={(e) => {
              setIsEditing(true);
              setEditValue(e.target.value.replace(/[^0-9]/g, ''));
            }}
            onFocus={() => {
              setEditValue(String(currentPage + 1));
              setIsEditing(true);
            }}
            onBlur={handleCommitEdit}
            onKeyDown={handleKeyDown}
            placeholder={`${formatNumber(currentPage + 1)} / ${formatNumber(totalPages)}`}
            className="w-16 h-8 text-center text-sm rounded-md border border-foreground/30 bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring ml-1"
            title="Type a page number"
          />
        )}

        <Button variant="outline" size="icon" onClick={handleNext} disabled={!hasNext || isLoading}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange?.(totalPages - 1)}
          disabled={!totalIsKnown || !hasNext || isLoading || !onPageChange}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Per page */}
      {onPageSizeChange && (
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <span className="text-sm text-muted-foreground whitespace-nowrap hidden md:inline">
            Show
          </span>
          <div className="relative">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-7 sm:h-8 appearance-none rounded-md border border-foreground/30 bg-background pl-2 pr-7 sm:pl-3 sm:pr-8 text-xs sm:text-sm font-medium text-foreground transition-colors hover:border-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
            >
              {pageSizeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
          </div>
          <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
            per page
          </span>
        </div>
      )}
    </div>
  );
};

export default OffsetPagination;
